import numeral from "numeral";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Plot from "react-plotly.js";
import { getBinaryData } from "../api/data";
import { ExploreContext, GetBinaryDataProps } from "../context/explore";
import { ExploreVector, UserCustomDataPoint } from "../core/global";
import { BinaryDecodeError, decodeTimeline, decodeVector } from "../helpers/binary";
import { getExploreColumnKeys } from "../helpers/core";
import { tickFormatStops } from "../helpers/graphs";
import styles from "../styles/components/explore-plot-timeseries.module.scss";
import { ExploreSecondarySourceSelect } from "./explore-secondary-source-select";
import { ExploreSelect } from "./explore-select";
import Loader from "./loader";

interface CombinedPoint {
  x: number;
  y: number;
}


type OverlapResult<T = number> = {
  timeline: number[];
  data: T[];
};

/**
 * Timelines are milliseconds since the epoch rather than ISO strings.
 *
 * The previous form parsed every entry with `new Date(...)` on both sides of the
 * comparison, twice per recompute, over a series that can run to hundreds of
 * thousands of points. Numbers compare directly.
 */
export function getOverlappingSecondaryData<T = number>(
  primaryTimeline: number[],
  secondaryTimeline: number[],
  secondaryData: T[]
): OverlapResult<T> | null {
  if (
    primaryTimeline.length === 0 ||
    secondaryTimeline.length === 0 ||
    secondaryData.length === 0
  ) {
    return { timeline: [], data: [] };
  }

  if (secondaryTimeline.length !== secondaryData.length) {
    return null
  }

  /*
   * One pass for the bounds rather than sorting a copy of the whole timeline. The
   * series is chronological already, but a min/max scan costs nothing and does not
   * assume it.
   */
  let minPrimary = primaryTimeline[0];
  let maxPrimary = primaryTimeline[0];
  for (const time of primaryTimeline) {
    if (time < minPrimary) minPrimary = time;
    if (time > maxPrimary) maxPrimary = time;
  }

  const resultTimeline: number[] = [];
  const resultData: T[] = [];

  secondaryTimeline.forEach((time, idx) => {
    if (time >= minPrimary && time <= maxPrimary) {
      resultTimeline.push(time);
      resultData.push(secondaryData[idx]);
    }
  });

  return {
    timeline: resultTimeline,
    data: resultData,
  };
}
interface ExploreTimeseriesPlotProps {
}

export const ExploreTimeseriesPlot = ({
}: ExploreTimeseriesPlotProps) => {

  const [secondaryDataIsLoading, setSecondaryDataIsLoading] = useState(false)
  const [exploreSecondaryData, setExploreSecondaryData] = useState<(number | null)[] | null>([])
  const [currentSecondaryKey, setCurrentSecondaryKey] = useState<string[] | null>(null)

  // Milliseconds since the epoch. Plotly reads these directly on a date axis.
  const [timelineSecondaryData, setTimelineSecondaryData] = useState<number[]>([])

  const [dataIsLoading, setDataIsLoading] = useState(false)
  const [currentKey, setCurrentKey] = useState<string[] | null>(null)
  const [timelineData, setTimelineData] = useState<number[]>([])
  const [exploreDataError, setExploreDataError] = useState<string[]>([])
  const [exploreData, setExploreData] = useState<(number | null)[]>([])
  const [plotDataUnits, setPlotDataUnits] = useState<string>("")
  const [plotTitle, setPlotTitle] = useState<string>("")
  const [directional, setDirectional] = useState<boolean>(false)
  // const [plotTitle, setPlotTitle] = useState<string>("")
  const { 
    loading,
    exploreDataSchema,
    exploreLocationParams,
    isPurchasedPoint,
    customDataPoints,

    secondaryDataSchema,
    normaliseTimeline,
    currentCustomPoint,
    linkSecondaryVectors,
  } = useContext(ExploreContext);
  
  const getPlotDataUnits = useCallback((currentKey: string[] | null): [string, string, boolean] => {
    if (currentKey !== null && exploreDataSchema !== null) {
      const {vectors, description: {
        lat,
        lon
      }} = exploreDataSchema;
    
      const option = vectors.find((v: ExploreVector) => v.key === currentKey[0]);
      const units = option?.units || "";
      const directional = option?.directional || false;
      const lonStr = lon.toFixed(5);
      const latStr = lat.toFixed(5);
      const plotTitle = `${currentKey} at ${latStr}, ${lonStr}`
      return [units, plotTitle, directional]
    }
    return ["", "", false];
  },[JSON.stringify(exploreDataSchema), JSON.stringify(currentKey)])

  const keyExistsInSecondarySchema = useMemo(() => {
    if (linkSecondaryVectors && secondaryDataSchema !== null && currentKey !== null) {
      const {vectors} = secondaryDataSchema;
      const found = vectors.find( ({key}) => {
        if (currentKey && currentKey.length > 0) {
          return key === currentKey[0]
        }
        return false
      })

      return found !== undefined
    }
    return null
  }, [JSON.stringify(currentKey), linkSecondaryVectors, JSON.stringify(secondaryDataSchema)])

  useEffect(() => {
    if (linkSecondaryVectors) {
      // currentSecondaryKey should match currentKey
      if (secondaryDataSchema !== null && currentKey !== null) {
        const {vectors} = secondaryDataSchema;
        const found = vectors.find( ({key}) => {
          if (currentKey && currentKey.length > 0) {
            return key === currentKey[0]
          }
          return false
        })
        if (found) {
          setCurrentSecondaryKey(found.key ? [found.key] : null)
        } else {
          // no vector, but visually we need to indicate when they are linked
          setCurrentSecondaryKey(currentKey)
        }
      } else {
        setCurrentSecondaryKey(null)
      }
    }
  }, [JSON.stringify(currentKey), linkSecondaryVectors, JSON.stringify(secondaryDataSchema)])

  // TODO: Timeline data could be generated for regular data with start_date and interval specified
  useEffect(() => {
    if (exploreLocationParams === null || exploreDataSchema === null) {
      return;
    }

    /*
     * The effect refires whenever the location or schema changes, and nothing used
     * to cancel the request already in flight. Two responses could settle out of
     * order and leave the older one on screen, which reads as the wrong location's
     * data rather than as a bug.
     */
    let cancelled = false;

    const execute = async (schemaParams: GetBinaryDataProps) => {
      const result = await getBinaryData({...schemaParams});
      if (cancelled) return;

      const { response, error, message } = result;

      if (error !== 200 && message) {
        setExploreDataError((m) => [...m, message]);
        return;
      }
      if (!response) return;

      try {
        const millis = await decodeTimeline(response);
        if (cancelled) return;
        setTimelineData(millis);
      } catch (err) {
        if (cancelled) return;
        setExploreDataError((m) => [
          ...m,
          err instanceof BinaryDecodeError ? err.message : "Could not read the timeline.",
        ]);
      }
    };

    const key = exploreDataSchema?.timeline.key || "Date/Time"
    const schemaParams = {
      ...exploreLocationParams, key, preview: !isPurchasedPoint, source: isPurchasedPoint ? "s3": "azure"
    }
    void execute(schemaParams);

    return () => { cancelled = true; };
  }, [isPurchasedPoint, JSON.stringify(exploreLocationParams), JSON.stringify(exploreDataSchema)]);
  
  useEffect(() => {
    let cancelled = false;

    if (currentKey !== null && exploreLocationParams !== null) {
      const execute = async (schemaParams: GetBinaryDataProps) => {
        try {
          const result = await getBinaryData({...schemaParams});
          if (cancelled) return;

          const { response, error, message } = result;
          if (error !== 200 && message) {
            setExploreDataError((m) => [...m, message]);
            setExploreData([])
            return;
          }
          if (!response) return;

          const values = await decodeVector(response);
          if (cancelled) return;

          setExploreData(values)
          const [units, plotTitle, directional] = getPlotDataUnits(currentKey)
          setPlotDataUnits(units || "")
          setPlotTitle(plotTitle || "")
          setDirectional(directional || false)
        } catch (err) {
          if (cancelled) return;
          setExploreDataError((m) => [
            ...m,
            err instanceof BinaryDecodeError ? err.message : "Could not read the data vector.",
          ]);
          setExploreData([])
        } finally {
          /*
           * In the finally, and after the decode. Previously this ran as soon as the
           * request returned, while the FileReader was still working - so the
           * spinner cleared before there was anything to draw, and stayed clear if
           * the decode then failed.
           */
          if (!cancelled) setDataIsLoading(false);
        }
      };

      if (currentKey && Array.isArray(currentKey) && currentKey.length > 0) {
        const schemaParams = {
          ...exploreLocationParams, key: currentKey[0], preview: !isPurchasedPoint, source: isPurchasedPoint ? "s3": "azure"
        }
        void execute(schemaParams);
      }
    }

    return () => { cancelled = true; };
  }, [JSON.stringify(currentKey), JSON.stringify(exploreLocationParams), isPurchasedPoint ])

  // Load secondary data
  useEffect(() => {
    if (secondaryDataSchema === null || currentCustomPoint === null) {
      return;
    }

    let cancelled = false;

    if (exploreLocationParams !== null && secondaryDataSchema !== null) {
      const execute = async (params: UserCustomDataPoint, key: string) => {
        setSecondaryDataIsLoading(true)
        try {
          const result = await getBinaryData({
            ...params,
            orderId: params?.container || '',
            key,
          });
          if (cancelled) return;

          const { response, error, message } = result;
          if (error !== 200 && message) {
            setExploreDataError((m) => [...m, message]);
            setExploreSecondaryData([])
            return;
          }
          if (!response) return;

          const values = await decodeVector(response);
          if (cancelled) return;
          setExploreSecondaryData(values)
        } catch (err) {
          if (cancelled) return;
          setExploreDataError((m) => [
            ...m,
            err instanceof BinaryDecodeError ? err.message : "Could not read the secondary data.",
          ]);
          setExploreSecondaryData([])
        } finally {
          if (!cancelled) setSecondaryDataIsLoading(false)
        }
      };
      if (keyExistsInSecondarySchema === false) {
        setExploreSecondaryData(null)
      } else if (currentSecondaryKey && Array.isArray(currentSecondaryKey) && currentSecondaryKey.length > 0) {
        const key = currentSecondaryKey[0]
        void execute({
          ...currentCustomPoint,
        }, key);
      }
    }

    return () => { cancelled = true; };
  }, [JSON.stringify(secondaryDataSchema), keyExistsInSecondarySchema, JSON.stringify(currentSecondaryKey) ])

  // Load timeline for secondary data
  useEffect(() => {
    if (secondaryDataSchema === null || currentCustomPoint === null) {
      return;
    }

    let cancelled = false;

    const execute = async (params: UserCustomDataPoint, key: string) => {
      setSecondaryDataIsLoading(true)
      try {
        const result = await getBinaryData({
          ...params,
          orderId: params?.container || '',
          key,
        });
        if (cancelled) return;

        const { response, error, message } = result;
        if (error !== 200 && message) {
          // Deliberately quiet: a custom point without a timeline is not an error
          // worth showing, and the plot falls back to the primary series.
          return;
        }
        if (!response) return;

        const millis = await decodeTimeline(response);
        if (cancelled) return;
        setTimelineSecondaryData(millis);
      } catch (err) {
        if (cancelled) return;
        setExploreDataError((m) => [
          ...m,
          err instanceof BinaryDecodeError ? err.message : "Could not read the secondary timeline.",
        ]);
      } finally {
        if (!cancelled) setSecondaryDataIsLoading(false)
      }
    };

    const key = secondaryDataSchema?.timeline.key || "Date/Time"
    void execute({
      ...currentCustomPoint,
    }, key);

    return () => { cancelled = true; };
  }, [JSON.stringify(secondaryDataSchema) ])
  
  const columnKeys = useMemo(() => {
    if (exploreDataSchema !== null) {
      return getExploreColumnKeys(
        exploreDataSchema,
        'all'
      )
    }
    return []
  }, [exploreDataSchema])

  const isLoading =
    timelineData.length === 0 ||
    exploreData.length === 0 ||
    exploreDataSchema === null ||
    columnKeys.length === 0 ||
    currentKey === null ||
    dataIsLoading ||
    loading;

  
  const {useTimeline, useData} = useMemo(() => {
    if (normaliseTimeline 
      && timelineData.length > 0 
      && timelineSecondaryData.length > 0 
      && exploreSecondaryData 
      && exploreSecondaryData.length > 0
    ) {
      const result = getOverlappingSecondaryData(timelineData, timelineSecondaryData, exploreSecondaryData)
      if (result) {
        const {timeline, data} = result
        return {useTimeline: timeline, useData: data}
      }
    }
    return {useTimeline: timelineSecondaryData, useData: exploreSecondaryData}
  }, [timelineSecondaryData, timelineData, normaliseTimeline, exploreSecondaryData])

  const secondaryDataTitle = useMemo(() => {
    if (currentCustomPoint) {
      const {lat, lon, distance} = currentCustomPoint
      return `lat: ${lat} lon: ${lon} - dist: ${numeral(distance).format('0.0a') }m from lat: ${exploreLocationParams?.lat} lon: ${exploreLocationParams?.lon}`
    }
    return ''
  }, [JSON.stringify(currentCustomPoint)])
  
  const showSecondYAxis = useMemo(() => {
    return (currentKey !== null 
      && currentSecondaryKey !== null 
      && currentSecondaryKey !== null 
      && currentSecondaryKey[0] !== currentKey[0]
    )
  }, [JSON.stringify(currentSecondaryKey), JSON.stringify(currentKey)])

  const {secondaryDataVectorDirectional, secondaryDataVectorUnits, secondaryDataVectorTitle} = useMemo(() => {
    if (currentSecondaryKey !== null) {
      if (secondaryDataSchema !== null && currentSecondaryKey !== null) {
        const {vectors} = secondaryDataSchema;
        const found = vectors.find( ({key}) => {
          if (currentSecondaryKey && currentSecondaryKey.length > 0) {
            return key === currentSecondaryKey[0]
          }
          return false
        })
        if (found) {
          const {directional, units, name} = found
          return {
            secondaryDataVectorDirectional: directional, 
            secondaryDataVectorUnits: units, 
            secondaryDataVectorTitle: name}
        }
      }
    }
    return {}
  } ,[JSON.stringify(currentSecondaryKey), JSON.stringify(secondaryDataSchema)])

  const showTooltips = useMemo(() => {
    // HACK: Disable tooltips for certain plots where they cause issues
    const show = !(directional || (currentKey && currentKey.length > 0 && 
        (currentKey[0] === "Sea Ice Area Fraction"
          || currentKey[0] === "Sea Surface Temperature"
          || currentKey[0] === "Wave Orbital Velocity (Nearbed)"))
      )

    const showSecondary = !secondaryDataVectorDirectional
    return !!(show && showSecondary)
  }, [directional, currentKey, secondaryDataVectorDirectional, secondaryDataVectorDirectional])
  
  const showSecondaryDataSource = useMemo(() => {
    const enableCustomData = import.meta.env.VITE_ENABLE_CUSTOM_DATA === 'true'
    return enableCustomData
  }, [])

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '16px', position: "relative" }}>
        <div className={styles.row}>
          <ExploreSelect 
            columnKeys={columnKeys} 
            onChangeKey={(key: string[] | null) => {
              setDataIsLoading(true)
              setCurrentKey(key)
              setExploreData([])
            }}
          />
        </div>
        {showSecondaryDataSource && <ExploreSecondarySourceSelect 
          currentKey={currentSecondaryKey}
          onChangeKey={(key: string[] | null) => {
            // setSecondaryDataIsLoading(true)
            if (!linkSecondaryVectors) {
              setCurrentSecondaryKey(key)
            }
            // setExploreSecondaryData([])
          }}
        />
        }
        {!showTooltips && 
          <p className={styles.note}>
            <strong>Note:</strong> Tooltips are disabled for this plot type.
          </p>}
      </div>
      {isLoading ? (
        <div style={{ width: "100%", padding: "240px 0 64px 0" }}>
          <Loader center showCog />
        </div>
      ) : (
        <div>
          <div style={{ height: "550px", width: "100%", position: "relative" }}>
            <Plot
              data={[
                (exploreSecondaryData && useTimeline && useData) ? {
                  x: useTimeline,
                  y: useData,
                  type: "scattergl",
                  mode: "lines",
                  name: `Location ${secondaryDataTitle}`,
                  hovertemplate: `%{x|%d-%b-%Y %H:%M}<br />%{y:.2f} ${plotDataUnits}<extra></extra>`,
                  line: {
                    color: "rgba(255, 127, 1, 0.9)",
                    // dash: "dot",
                    // color: "rgba(100, 100, 100, 0.9)",
                  },
                  yaxis: showSecondYAxis ? 'y2' : undefined,
                  // line: {
                  //   dash: "dot", // visually distinguish it
                  // },
                } : {},
                {
                  x: timelineData,
                  y: [...exploreData],
                  type: "scattergl",
                  mode: "lines",
                  name: (exploreSecondaryData && timelineSecondaryData) ? "Primary data source" : undefined,
                  hovertemplate: `%{x|%d-%b-%Y %H:%M}<br />%{y:.2f} ${plotDataUnits}<extra></extra>`,
                  line: {
                    color: "rgba(30, 120, 180, 0.9)",
                  },
                  yaxis: showSecondYAxis ? 'y1' : undefined,
                  // marker: { size: 3 },
                },
                
              ]}
              style={{
                width: "100%",
                height: "100%",
              }}
              config={{
                toImageButtonOptions: {
                  format: "png",
                  filename: plotTitle
                    .toLowerCase()
                    .replace(/\s/gi, "-"),
                  width: 1200,
                  height: 600,
                },
              }}
              // https://plotly.com/javascript/reference/layout/xaxis/
              layout={{
                hovermode: showTooltips ? "closest": false,
                title: {
                  text: plotTitle,
                },
                showlegend: !!(exploreSecondaryData && timelineSecondaryData),
                legend: {
                  orientation: "h",
                  // xanchor: "bottom"
                },
                images: isPurchasedPoint ? undefined : [ {
                  source: "/mow-logo-inline-full.png",
                  xref: "paper",
                  yref: "paper",
                  x: -0.05,
                  y: -0.15,
                  xanchor: "left",
                  yanchor: "bottom",
                  sizex: 0.2,
                  sizey: 0.2,
                  opacity: 1,
                  layer: "above"
                },],
                yaxis: {
                  range: directional ? [0, 360] : undefined,
                  dtick: directional ? 15 : undefined,
                  showgrid: true,
                  title: {
                    text: showSecondYAxis? `${currentKey} [${plotDataUnits}]` : plotDataUnits,
                    position: "middle center",
                  },
                },
                xaxis: {
                  /*
                   * Explicit, because the timeline is now milliseconds rather than
                   * ISO strings. Plotly infers a date axis from strings but a linear
                   * one from numbers, which would plot the series against raw epoch
                   * values.
                   */
                  type: "date",
                  tickformatstops: [...tickFormatStops],
                },
                yaxis2: showSecondYAxis ?
                  {
                    range: secondaryDataVectorDirectional ? [0, 360] : undefined,
                    dtick: secondaryDataVectorDirectional ? 15 : undefined,
                    side: 'right',
                    showgrid: true,
                    title: {
                      text: secondaryDataVectorTitle ? `${secondaryDataVectorTitle} [${secondaryDataVectorUnits}]` : '',
                      position: "middle center",
                    },
                  }
                  : {}
              }}
            />
            
            {/* {isLoading && (
              <div style={{ width: "100%", position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "rgba(255, 255, 255, 0.5)" }}>
                <Loader center showCog />
              </div>
            )} */}
          </div>
        </div>
      )}
    </>
  );
};